import {
  Box,
  Fade,
  ListItemText,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
} from '@mui/material';
import type { EditorState } from 'draft-js';
import { Delayed, EditTemplate, Switch } from 'klayowebshared';
import { cloneDeep, get, isEmpty, uniqBy } from 'lodash';
import React, { Component, SyntheticEvent } from 'react';
import { AppContext } from '../../common/AppContext';
import axiosClient from '../../common/AxiosClient';
import { ErrorModel } from '../../common/models/ErrorModel';
import { Alert } from '../../components/Alert';
import { AttributeActionMenu } from '../../components/AttributeActionMenu';
import { Button } from '../../components/Button';
import { PaperOrDialog } from '../../components/PaperOrDialog';
import { RichTextEditor } from '../../components/RichTextEditor';
import { TablePlaceholder } from '../../components/TablePlaceholder';
import { TextField } from '../../components/TextField';
import { DomainSelector } from '../../components/selectors/DomainSelector';
import { AttributeTable } from '../../components/table/AttributeTable';
import { RelatedTaskInAttributeDialog } from '../../components/table/RelatedTasksInAttributeDialog';
import { TasksTable } from '../../components/table/TasksTable';
import { DOMAIN_BLANK } from '../../constants/constants';
import { Data } from '../../data/Data';
import { SettingsAttribute, SettingsAttributeData } from '../../data/settings/SettingsAttribute';
import { SettingsJob } from '../../data/settings/SettingsJob';
import { SettingsTask } from '../../data/settings/SettingsTask';
import { withLocale } from '../../providers/LocaleProvider';
import AddIcon from '../../resources/images/icons-add.svg';
import LeftIcon from '../../resources/images/icons-arrow-left.svg';
import RightIcon from '../../resources/images/icons-arrow-right.svg';
import AttributesIcon from '../../resources/images/icons-attribute.svg';
import CheckListIcon from '../../resources/images/icons-checklist.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import type {
  AddEditJobViewProps,
  AddEditJobViewState,
  AttributeActionMenuRef,
  ISettingsJob,
  JobDefinitionData,
  JobDefinitionDomain,
  RequirementType,
} from '../../types/models/settings.types';
import { trimToLowercase } from '../../utilities';
import { AttributeSelectorDialog } from './AttributeSelectorDialog';
import { ConfirmDeleteTaskDialog } from './ConfirmDeleteTaskDialog';
import { TaskSelectorDialog } from './TaskSelectorDialog';

class AddEditJobViewBase extends Component<AddEditJobViewProps, AddEditJobViewState> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  existingJob: SettingsJob;
  setMenuRef: (menu: AttributeActionMenuRef | null) => void;
  menu: AttributeActionMenuRef | null = null;

  constructor(props: AddEditJobViewProps) {
    super(props);

    this.state = {
      job: props.job ?? null,
      saving: false,
      error: props.error ?? null,
      step: 0,
      showAttributeSelectDialog: false,
      searchTask: null,
      searchCompetency: null,
      descriptionEditorState: RichTextEditor.createEditorState(''),
      originalEditorState: RichTextEditor.createEditorState(''),
      hasEdit: false,
      isShowTasksSelectDialog: false,
      isShowRelatedTasksInCompetencyDialog: false,
      tasksHasAttribute: [],
      isRemoveRelatedTasksInCompetencyDialog: false,
      deletingRowError: null,
      selectedDeleteTask: null,
      selectedAttr: null,
      domains: [],
      selectedDomain: DOMAIN_BLANK as unknown as JobDefinitionDomain,
      loading: false,
      jobSuggestion: [],
      isLoadingJobSuggestions: false,
    };

    this.existingJob = new SettingsJob((props.job as JobDefinitionData) ?? undefined);

    this.setMenuRef = (menu: AttributeActionMenuRef | null) => {
      this.menu = menu;
    };
  }

  componentDidMount(): void {
    this.loadDetails();
    this.loadDomains();
    this.loadSelectedTasks();
    this.loadSelectedCompetencies();
  }

  componentDidUpdate(prevProps: AddEditJobViewProps, prevState: AddEditJobViewState): void {
    if (this.props.error !== prevProps.error) this.setState({ error: this.props.error ?? null });
    const { domains, job } = this.state;

    if (
      (prevState.domains !== domains ||
        prevState.job?.jobDefinitionDomainId !== job?.jobDefinitionDomainId) &&
      !isEmpty(domains) &&
      !isEmpty(job)
    ) {
      const selectedDomain = (domains.find((d) => d.id === job.jobDefinitionDomainId) ||
        DOMAIN_BLANK) as JobDefinitionDomain;
      this.setState({ selectedDomain });
    }
  }

  loadDetails(): void {
    const { job, step } = this.state;
    const jobDefinitionId = job ? job.jobDefinitionId : this.props.jobId;

    if (!jobDefinitionId) return;

    this.context.setLoading?.('loading', true);

    Data.get<{ jobDefinition: JobDefinitionData }>(
      this.context,
      '/JobDefinition/' + jobDefinitionId
    )
      .then((response) => {
        const job = new SettingsJob(response.data.jobDefinition);
        this.existingJob = new SettingsJob(response.data.jobDefinition);

        this.setState({
          job: job,
          descriptionEditorState: RichTextEditor.createEditorState(job.description ?? ''),
          originalEditorState: RichTextEditor.createEditorState(job.description ?? ''),
        });
      })
      .catch((e: unknown) => {
        this.setState({ error: ErrorModel.parseServerError(e) });
      })
      .finally(() => {
        this.context.setLoading?.('loading', false);
      });
  }

  loadDomains(): void {
    this.setState({ loading: true });
    Data.get<{
      jobDefinitionDomain: JobDefinitionDomain[];
    }>(this.context, '/JobDefinition/Domains')
      .then((response) => {
        const domainList = response.data.jobDefinitionDomain;

        const domains = [DOMAIN_BLANK, ...domainList];

        this.setState({
          domains,
          loading: false,
        });
      })
      .catch((e: unknown) => {
        this.setState({ error: ErrorModel.parseServerError(e), domains: [], loading: false });
      });
  }

  async loadJobDetailAsync(jobId: string): Promise<JobDefinitionData> {
    const response = await Data.get<{ jobDefinition: JobDefinitionData }>(
      this.context,
      `/StandardLibrary/jobs/${jobId}`
    );
    return response.data.jobDefinition;
  }

  async loadSelectedTasks(): Promise<void> {
    const taskIds = new URLSearchParams(location.search).get('taskIds')?.split(',');

    if (isEmpty(taskIds)) return;

    try {
      const response = await axiosClient.post(
        '/Regulation/Task/GetByIds',
        {
          taskIds,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data) {
        const tasks = (response.data as { tasks: unknown[] }).tasks;
        this.setState({
          job: {
            ...this.state.job,
            regulationTasks: tasks.map((task: unknown) => new SettingsTask(task)),
          } as ISettingsJob,
        });
      }
    } catch (_error) {
      // Error handling
    }
  }

  async loadSelectedCompetencies(): Promise<void> {
    const competencyIds = new URLSearchParams(location.search).get('competencyIds')?.split(',');

    if (isEmpty(competencyIds)) return;

    try {
      const response = await axiosClient.post<{ competencies: SettingsAttributeData[] }>(
        '/AttributeDefinition/GetByIds',
        {
          competencyIds,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data) {
        const competencies = response.data.competencies;
        this.setState({
          job: {
            ...this.state.job,
            attributes: competencies.map((c) => new SettingsAttribute(c)),
          } as ISettingsJob,
        });
      }
    } catch (_error) {
      // Error handling
    }
  }

  onDomainChange(_event: SyntheticEvent, newValue: JobDefinitionDomain | null): void {
    const { job } = this.state;
    if (job) {
      job.jobDefinitionDomainId = newValue?.id ? newValue : null;
      this.setState({
        job,
        selectedDomain: (newValue ?? DOMAIN_BLANK) as JobDefinitionDomain,
      });
    }
  }

  onNameChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { job } = this.state;
    if (job) {
      job.name = e.target.value;
      this.setState({ job });
    }
  }

  onDescriptionChange(text: string): void {
    const { job } = this.state;
    if (job) {
      job.description = text;
      this.setState({ job });
    }
  }

  onDescriptionEditorStateChange(descriptionEditorState: EditorState): void {
    this.setState({ descriptionEditorState });
  }

  isValid(job: ISettingsJob | null, step: number): boolean {
    if (!job || !this.isNameValid(job)) return false;
    if (step === 0) return true;
    if (step === 1) return true;
    return this.isAttributesValid(job);
  }

  isNameValid(job: ISettingsJob | null): boolean {
    return job != null && job.name != null && job.name.length > 2;
  }

  isAttributesValid(job: ISettingsJob | null): boolean {
    if (!job) return false;
    let isValid = true;

    job.attributes.forEach((a) => {
      if (a.isParent !== true && a.isChild !== true && !a.requirementType) isValid = false;
    });

    return isValid;
  }

  onSave(): void {
    const { onSave, editMode, onBlockNavigation } = this.props;
    const { job, step } = this.state;

    if (!editMode && step === 0 && this.isValid(job, 0)) this.setState({ step: 1 });
    if (!editMode && step === 1) this.setState({ step: 2 });

    if (editMode && step === 1) {
      // check requirementType in each attribute in job not undefined
      if (!this.isAttributesValid(job)) {
        this.setState({ step: 2 });
        return;
      }
    }

    if (onSave && job && (editMode || step === 2)) {
      if (onBlockNavigation) onBlockNavigation(false, 'Job');
      this.setState({ saving: true });
      onSave(job);
    }
  }

  onShowAttributeSelectDialog(_e: SyntheticEvent): void {
    this.setState({ showAttributeSelectDialog: true });
  }

  onSearchTask(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    this.setState({ searchTask: e.target.value });
  }

  onSearchCompetency(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    this.setState({ searchCompetency: e.target.value });
  }

  onAttributesAdded(addedAttributes: SettingsAttribute[]): void {
    const { job } = this.state;
    if (!job) return;

    const childAttrs: SettingsAttribute[] = [];
    addedAttributes.forEach((a) => {
      a.childAttributes.forEach((c) => {
        childAttrs.push(c);
        if (
          !addedAttributes.some((attr) => attr.attributeDefinitionId === c.attributeDefinitionId) &&
          !job.attributes.some((attr) => attr.attributeDefinitionId === c.attributeDefinitionId)
        )
          addedAttributes.push(c.toStandardAttribute());
      });
    });
    job.attributes = [...job.attributes, ...addedAttributes, ...childAttrs];
    this.setState({ job, showAttributeSelectDialog: false });
  }

  onAttributesEdit(addedAttributes: SettingsAttribute[]): void {
    const { job } = this.state;
    if (!job) return;

    const childAttrs: SettingsAttribute[] = [];
    addedAttributes.forEach((a) => {
      a.childAttributes.forEach((c) => {
        childAttrs.push(c);
        if (
          !addedAttributes.some((attr) => attr.attributeDefinitionId === c.attributeDefinitionId) &&
          !job.attributes.some((attr) => attr.attributeDefinitionId === c.attributeDefinitionId)
        )
          addedAttributes.push(c.toStandardAttribute());
      });
    });
    job.attributes = [...addedAttributes, ...childAttrs];
    this.setState({ job, showAttributeSelectDialog: false });
  }

  onTabChange(_e: SyntheticEvent, newStep: number): void {
    this.setState({ step: newStep, error: null });
  }

  onAttributeDelete(_e: SyntheticEvent, attr: SettingsAttribute): void {
    const { job } = this.state;
    if (!job) return;

    const { regulationTasks } = job;

    // Check if the attribute is used in any task
    const isUsedInTask = regulationTasks.some((task: any) => {
      return task.competencies.some(
        (c: any) => c.attributeDefinitionId === attr.attributeDefinitionId
      );
    });

    // Get list tasks has the attribute
    const tasksHasAttributeData = regulationTasks.filter((task: any) => {
      return task.competencies.some(
        (c: any) => c.attributeDefinitionId === attr.attributeDefinitionId
      );
    });

    if (isUsedInTask) {
      this.setState({
        isShowRelatedTasksInCompetencyDialog: true,
        tasksHasAttribute: tasksHasAttributeData,
      });
      return;
    }

    job.attributes = job.attributes.filter(
      (a) =>
        !(a.attributeId === attr.attributeId && !a.isChild) &&
        a.parentAttributeId !== attr.attributeId
    );

    this.setState({ job });
  }

  onAttributeRequirementChange(
    attribute: SettingsAttribute,
    requirementType: RequirementType
  ): void {
    attribute.requirementType = requirementType;
    attribute.type = requirementType.label;
    this.forceUpdate();
  }

  onCloseAttributeSelectDialog(): void {
    this.setState({ showAttributeSelectDialog: false });
  }

  onBack(): void {
    const { step } = this.state;
    this.setState({ step: step - 1 });
  }

  onNavigation(e: SyntheticEvent, callback: (e: SyntheticEvent) => void): void {
    const { onNavigation } = this.props;
    if (onNavigation) onNavigation(e, callback);
  }

  onCancel(e: SyntheticEvent, _source?: string): void {
    this.onNavigation(e, this.stopBlockingNavAndClose.bind(this));
  }

  stopBlockingNavAndClose(e: SyntheticEvent): void {
    const { onCancel, onBlockNavigation } = this.props;
    if (onBlockNavigation) onBlockNavigation(false, 'Job');
    if (onCancel) onCancel(e);
  }

  rowHasAction(_attr: SettingsAttribute): boolean {
    return true;
  }

  rowActionDisabled(attr: SettingsAttribute): boolean {
    const { job } = this.state;
    if (!job) return false;
    return (
      job.attributes.some(
        (a) => a.isParent && a.childAttributes.some((a2) => a2.attributeId === attr.attributeId)
      ) !== false
    );
  }

  onEditStateChange(hasEdit: boolean): void {
    this.setState({ hasEdit });
  }

  compareName(name: string | null | undefined, search: string | null): boolean {
    return String(name || '')
      ?.toLowerCase()
      ?.includes(trimToLowercase(search || ''));
  }

  onCloseTasksSelectDialog(): void {
    this.setState({ isShowTasksSelectDialog: false });
  }

  onShowTasksSelectDialog(): void {
    this.setState({ isShowTasksSelectDialog: true });
  }

  onTasksAdded(tasks: SettingsTask[], additionalAttributes: SettingsAttribute[] = []): void {
    const { job } = this.state;
    if (!job) return;

    job.regulationTasks = [...tasks];
    this.setState({ job });

    const listRelatedAttributes: SettingsAttribute[] = [];

    tasks.forEach((task) => {
      task.competencies.forEach((a: any) => {
        listRelatedAttributes.push(a);
      });
    });

    // remove duplicate attributes
    const uniqueAttributes = uniqBy(
      [...job.attributes, ...listRelatedAttributes, ...additionalAttributes],
      'attributeDefinitionId'
    ).map((a: any) => {
      return new SettingsAttribute(a as any);
    });

    this.onAttributesEdit(uniqueAttributes);
    this.onCloseTasksSelectDialog();
  }

  onDeleteTask(_action: string, selectedTask: SettingsTask): void {
    const { job } = this.state;
    if (!job) return;

    const { editMode } = this.props;

    const filterRegulationTaskList = job.regulationTasks.filter((r) => r.id !== selectedTask.id);
    // Remove related competency in task
    const existingCompetencies = job.attributes;
    const deletedCompetencies = selectedTask.competencies;

    if (editMode) {
      this.setState({ selectedDeleteTask: selectedTask });
      // Check if the task is used in any competency
      const isUsedInTask = existingCompetencies.some((a: any) =>
        deletedCompetencies.some((d: any) => d.attributeDefinitionId === a.attributeDefinitionId)
      );

      if (isUsedInTask) {
        this.onShowRemoveRelatedTasksInCompetencyDialog();
        return;
      }
    }

    let filterCompetencies: SettingsAttribute[] = [];
    filterCompetencies = existingCompetencies.filter(
      (a: any) =>
        !deletedCompetencies.some((d: any) => d.attributeDefinitionId === a.attributeDefinitionId)
    );

    filterRegulationTaskList.forEach((task: any) => {
      existingCompetencies.forEach((competency: any) => {
        // Check if any competency is still exist in the task, push it to the filterCompetencies
        if (
          task.competencies.some(
            (c: any) => c.attributeDefinitionId === competency.attributeDefinitionId
          )
        ) {
          filterCompetencies.push(competency);
        }
      });
    });

    filterCompetencies = uniqBy(filterCompetencies, 'attributeDefinitionId');

    this.setState({
      job: {
        ...job,
        regulationTasks: filterRegulationTaskList,
        attributes: filterCompetencies,
      } as ISettingsJob,
    });
  }

  onRemoveRelatedTasksInCompetency(): void {
    const { job, selectedDeleteTask } = this.state;
    if (!job || !selectedDeleteTask) return;

    const filterRegulationTaskList = job.regulationTasks.filter(
      (r: any) => r.id !== selectedDeleteTask.id
    );
    // Remove related competency in task
    const existingCompetencies = job.attributes;
    const deletedCompetencies = selectedDeleteTask.competencies;

    let filterCompetencies: SettingsAttribute[] = [];
    filterCompetencies = existingCompetencies.filter(
      (a: any) =>
        !deletedCompetencies.some((d: any) => d.attributeDefinitionId === a.attributeDefinitionId)
    );

    filterRegulationTaskList.forEach((task: any) => {
      existingCompetencies.forEach((competency: any) => {
        // Check if any competency is still exist in the task, push it to the filterCompetencies
        if (
          task.competencies.some(
            (c: any) => c.attributeDefinitionId === competency.attributeDefinitionId
          )
        ) {
          filterCompetencies.push(competency);
        }
      });
    });

    filterCompetencies = uniqBy(filterCompetencies, 'attributeDefinitionId');

    this.setState({
      job: {
        ...job,
        regulationTasks: filterRegulationTaskList,
        attributes: filterCompetencies,
      } as ISettingsJob,
    });
    this.onHideRemoveRelatedTasksInCompetencyDialog();
  }

  onRemoveRelatedTasksButKeepCompetency(): void {
    const { job, selectedDeleteTask } = this.state;
    if (!job || !selectedDeleteTask) return;

    const filterRegulationTaskList = job.regulationTasks.filter(
      (r) => r.id !== selectedDeleteTask.id
    );
    this.setState({ job: { ...job, regulationTasks: filterRegulationTaskList } as ISettingsJob });
    this.onHideRemoveRelatedTasksInCompetencyDialog();
  }

  onCloseRelatedTasksInCompetencyDialog(): void {
    this.setState({ isShowRelatedTasksInCompetencyDialog: false });
  }

  onShowRelatedTasksInCompetencyDialog(): void {
    this.setState({ isShowRelatedTasksInCompetencyDialog: true });
  }

  isRegulationTasksSame(other: SettingsTask[] | null | undefined): boolean {
    const { job } = this.state;
    if (!other || !job) return false;
    if (other.length !== job.regulationTasks.length) return false;

    for (const o of other) {
      const match = job.regulationTasks.find((r) => r.id === o.id);
      if (!match) return false;
    }
    return true;
  }

  isAttributesSame(other: SettingsAttribute[] | null | undefined): boolean {
    const { job } = this.state;
    if (!other || !job) return false;
    const otherNoChildren = other.filter((a) => !a.isChild);
    if (other.length !== job.attributes.length) return false;

    for (const o of otherNoChildren) {
      const match = job.attributes.find((a) => a.attributeId === o.attributeId && !a.isChild);

      if (!match) return false;

      if (match.requirementType && o.requirementType) {
        if (
          (match.requirementType as RequirementType).index !==
          (o.requirementType as RequirementType).index
        )
          return false;
      }

      if (match.isMinimum !== o.isMinimum) return false;
    }
    return true;
  }

  onShowRemoveRelatedTasksInCompetencyDialog(): void {
    this.setState({ isRemoveRelatedTasksInCompetencyDialog: true });
  }

  onHideRemoveRelatedTasksInCompetencyDialog(): void {
    this.setState({ isRemoveRelatedTasksInCompetencyDialog: false });
  }

  onCloseActionMenu(): void {
    if (this.menu) this.menu.close();

    this.setState({
      selectedAttr: null,
    });
  }

  onAttributeAction(e: SyntheticEvent, attr: SettingsAttribute): void {
    if (this.menu) this.menu.open(e.target as HTMLElement);

    this.setState({
      selectedAttr: attr,
    });
  }

  onUpdateMinimum(attr: SettingsAttribute, isMark: boolean): void {
    const { job } = this.state;
    if (!job) return;

    const previousJob = cloneDeep(job);

    this.setState(
      {
        job: {
          ...job,
          attributes: job.attributes.map((a) => {
            if (a.attributeId === attr.attributeId) {
              const updateAttr = cloneDeep(a);
              updateAttr.isMinimum = isMark;
              return updateAttr;
            }
            return a;
          }),
        } as ISettingsJob,
      },
      () => {
        const currentJob = this.state.job;
        if (!currentJob) return;

        const hasChanges = currentJob.attributes.some((newAttr) => {
          const oldAttr = previousJob.attributes.find((a) => a.attributeId === newAttr.attributeId);
          return oldAttr && oldAttr.isMinimum !== newAttr.isMinimum;
        });

        if (hasChanges) {
          this.setState({ hasEdit: true });
        }
      }
    );
  }

  renderActionMenu(): React.ReactNode {
    const { selectedAttr } = this.state;
    if (!selectedAttr) return null;
    const translate = this.props.translate;

    return (
      <div>
        {this.props?.organization?.enableJobMatching && (
          <MenuItem onClick={() => this.onUpdateMinimum(selectedAttr, !selectedAttr.isMinimum)}>
            <ListItemText>
              {selectedAttr?.isMinimum
                ? translate('addEditJob.unmarkAsMinimum')
                : translate('addEditJob.markAsMinimum')}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={(e) => this.onAttributeDelete(e, selectedAttr)}>
          <ListItemText>{translate('common.delete')}</ListItemText>
        </MenuItem>
      </div>
    );
  }

  onEnableJobMatching(e: React.ChangeEvent<HTMLInputElement>): void {
    const { job } = this.state;
    if (!job) return;
    this.setState({ job: { ...job, enableJobMatching: e.target.checked } as ISettingsJob });
  }

  render() {
    const {
      organization,
      user,
      theme,
      attributes,
      editMode,
      onCreate,
      onLoadAttributes,
      onShowNewAttributeDialog,
      onAllowNavigation,
      onBlockNavigation,
    } = this.props;
    const {
      error,
      job,
      step,
      showAttributeSelectDialog,
      descriptionEditorState,
      hasEdit,
      saving,
      isShowTasksSelectDialog,
      isShowRelatedTasksInCompetencyDialog,
      tasksHasAttribute,
      isRemoveRelatedTasksInCompetencyDialog,
      searchCompetency,
      searchTask,
    } = this.state;

    const filterItemsAttribute = [
      { id: 'search', field: 'attribute', operator: 'contains', value: searchCompetency },
    ];
    const filterItemsTasks = [
      { id: 'search', field: 'name', operator: 'contains', value: searchTask },
    ];

    const filterCompetencies =
      job && job.attributes
        ? job.attributes?.filter((p: SettingsAttribute) => {
            const matchChildren = p?.childAttributes?.some((c: SettingsAttribute) =>
              this.compareName(c?.name, searchCompetency)
            );
            const matchAttribute = this.compareName(p?.name, searchCompetency);
            return matchAttribute || matchChildren;
          })
        : [];

    const filterJobTasks = (get(job, 'regulationTasks', []) as SettingsTask[]).filter(
      (task: SettingsTask) => {
        if (searchTask) {
          const keyword = trimToLowercase(searchTask || '');
          const taskName = trimToLowercase(task?.name || '');
          return taskName?.includes(keyword);
        } else {
          return true;
        }
      }
    );

    return (
      <EditTemplate
        theme={theme}
        name='Job'
        onAllowNavigation={onAllowNavigation}
        onBlockNavigation={onBlockNavigation}
        onEditStateChange={this.onEditStateChange.bind(this)}
        detectEdit={!saving}
        compare={{
          existing: this.existingJob,
          editing: job,
          members: [
            { name: 'name' },
            {
              name: 'description',
              detectEdit: (existing: ISettingsJob, editing: ISettingsJob, isNew?: boolean) => {
                if (isNew) return editing.description && editing.description.trim() !== '<p></p>';
                return (
                  existing.description !== editing.description &&
                  existing.description + '\n<p></p>' !== editing.description
                );
              },
            },
            {
              name: 'regulationTasks',
              detectEdit: (existing: ISettingsJob, _editing: ISettingsJob) =>
                !this.isRegulationTasksSame(existing.regulationTasks),
            },
            {
              name: 'attributes',
              detectEdit: (existing: ISettingsJob, _editing: ISettingsJob) =>
                !this.isAttributesSame(existing.attributes),
            },
            {
              name: 'jobDefinitionDomainId',
              detectEdit: (existing: ISettingsJob, editing: ISettingsJob) => {
                return existing.jobDefinitionDomainId !== editing.jobDefinitionDomainId;
              },
            },
            {
              name: 'enableJobMatching',
              detectEdit: (existing: ISettingsJob, editing: ISettingsJob) => {
                return existing.enableJobMatching !== editing.enableJobMatching;
              },
            },
          ],
        }}
      >
        <PaperOrDialog
          theme={theme}
          dialogToBreakpoint='md'
          disabledToBreakpoint='md'
          paperPadding={{ xs: '46px 24px', md: '60px' }}
          title={
            editMode
              ? this.props.translate('addEditJob.jobDetails')
              : this.props.translate('addEditJob.createJob')
          }
          titleSx={{ fontSize: { xs: '20px', md: '30px' }, fontWeight: 'bold' }}
          titleSize={1}
          backButton={editMode}
          onBack={this.onCancel.bind(this)}
        >
          <Stack direction='column' spacing={4}>
            {error && (
              <Alert severity='error' sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            {!editMode ? (
              <Stepper activeStep={step}>
                <Step sx={{ cursor: step !== 0 ? 'pointer' : 'default' }}>
                  <StepLabel>{this.props.translate('addEditJob.jobDetails')}</StepLabel>
                </Step>
                <Step sx={{ cursor: step !== 0 ? 'pointer' : 'default' }}>
                  <StepLabel>{this.props.translate('addEditJob.tasks')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{this.props.translate('addEditJob.competencies')}</StepLabel>
                </Step>
              </Stepper>
            ) : (
              <div>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    centered={true}
                    value={step}
                    onChange={this.onTabChange.bind(this)}
                    variant='fullWidth'
                    sx={{ width: '100%' }}
                  >
                    <Tab label={this.props.translate('addEditJob.information')} value={0} />
                    <Tab label={this.props.translate('addEditJob.tasks')} value={1} />
                    <Tab label={this.props.translate('addEditJob.competencies')} value={2} />
                  </Tabs>
                </Box>
              </div>
            )}

            {step === 0 && (
              <Fade in={true}>
                <Stack direction='column' spacing={4}>
                  <TextField
                    label={this.props.translate('addEditJob.jobTitleRequired')}
                    placeholder={this.props.translate('addEditJob.jobTitlePlaceholder')}
                    autoComplete={false}
                    autoFocus={true}
                    validationMethod={(_e?: SyntheticEvent, _value?: string) =>
                      this.isNameValid(job)
                    }
                    validationText={this.props.translate('addEditAttribute.validNameRequired')}
                    clearable={false}
                    sx={{ width: '100%' }}
                    value={job ? job.name : null}
                    onChange={this.onNameChange.bind(this)}
                  />

                  <RichTextEditor
                    label={this.props.translate('addEditJob.jobDescription')}
                    editorState={descriptionEditorState}
                    onChange={this.onDescriptionChange.bind(this)}
                    onChangeEditorState={this.onDescriptionEditorStateChange.bind(this)}
                  />

                  <DomainSelector<any>
                    label={this.props.translate('labels.domainOptional')}
                    value={this.state.selectedDomain}
                    items={this.state.domains || []}
                    callApi={false}
                    multiple={false}
                    getItemLabel={(option) =>
                      option.id === null
                        ? this.props.translate('placeholders.blank')
                        : option.name || ''
                    }
                    onChange={this.onDomainChange.bind(this)}
                    disabled={saving}
                    loading={this.state.loading}
                    disabledSearch={true}
                  />

                  <Switch
                    label={this.props.translate('addEditJob.enableJobMatchingForJob')}
                    checked={job?.enableJobMatching ?? false}
                    onChange={this.onEnableJobMatching.bind(this)}
                    disabled={!organization?.enableJobMatching}
                  />
                </Stack>
              </Fade>
            )}

            {step === 1 && (
              <Fade in={true}>
                <div>
                  <Stack direction='column' spacing={2}>
                    <Stack gap={2}>
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        spacing={2}
                      >
                        <TextField
                          value={searchTask}
                          dense={true}
                          placeholder={this.props.translate('addEditJob.searchTask')}
                          autoComplete={false}
                          leadingIcon={<SearchIcon />}
                          disabled={
                            get(job, 'regulationTasks', []).length > 0
                              ? job!.regulationTasks.length === 0
                              : true
                          }
                          onChange={this.onSearchTask.bind(this)}
                          sx={{ minWidth: '300px' }}
                        />

                        <Button
                          onClick={this.onShowTasksSelectDialog.bind(this)}
                          size='md'
                          variant='outlined'
                          startIcon={<AddIcon />}
                          label={this.props.translate('addEditJob.addTasks')}
                        />
                      </Stack>

                      <Delayed>
                        <TasksTable
                          rows={filterJobTasks as any}
                          pagination={false}
                          theme={theme}
                          showFooter={false}
                          rowHasAction={true}
                          filterItems={filterItemsTasks}
                          noRowsComponent={
                            <TablePlaceholder
                              text={this.props.translate('addEditJob.useAddTasksButton')}
                              icon={<CheckListIcon />}
                            />
                          }
                          dense={true}
                          paper={false}
                          columns={[
                            {
                              type: TasksTable.columns.name,
                              label: this.props.translate('tableColumns.task'),
                            },
                            {
                              type: TasksTable.columns.actions,
                              label: this.props.translate('tableColumns.actions'),
                            },
                          ]}
                          onPositionAction={this.onDeleteTask.bind(this) as any}
                          isUsingDeleteIcon={true}
                          keyword={searchTask ?? undefined}
                        />
                        {job!.numberOfRegulationTasks > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                            }}
                          >
                            {this.props.translate('addEditJob.totalTasks')}{' '}
                            {job!.numberOfRegulationTasks}
                          </Box>
                        )}
                      </Delayed>
                    </Stack>
                  </Stack>
                </div>
              </Fade>
            )}

            {step === 2 && (
              <Fade in={true}>
                <div>
                  <Stack direction='column' gap={2}>
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      spacing={2}
                    >
                      <TextField
                        value={searchCompetency}
                        dense={true}
                        placeholder={this.props.translate('addEditJob.searchCompetency')}
                        disabled={Boolean(job && job.attributes.length === 0)}
                        autoComplete={false}
                        leadingIcon={<SearchIcon />}
                        onChange={this.onSearchCompetency.bind(this)}
                        sx={{ minWidth: '300px' }}
                      />

                      <Button
                        onClick={this.onShowAttributeSelectDialog.bind(this)}
                        size='md'
                        variant='outlined'
                        startIcon={<AddIcon />}
                        label={this.props.translate('addEditJob.addCompetencies')}
                      />
                    </Stack>
                    <AttributeTable<SettingsAttribute>
                      rows={filterCompetencies}
                      pagination={false}
                      theme={theme}
                      translate={this.props.translate}
                      showFooter={false}
                      rowHasAction={this.rowHasAction.bind(this)}
                      rowActionDisabled={this.rowActionDisabled.bind(this)}
                      filterItems={filterItemsAttribute}
                      noRowsComponent={
                        <TablePlaceholder
                          text={this.props.translate('addEditJob.useAddCompetenciesButton')}
                          icon={<AttributesIcon />}
                        />
                      }
                      dense={true}
                      tree={true}
                      onAttributeDelete={this.onAttributeDelete.bind(this)}
                      onRequirementChange={this.onAttributeRequirementChange.bind(this)}
                      paper={false}
                      columns={[
                        {
                          type: AttributeTable.columns.attribute,
                          label: this.props.translate('tableColumns.competency'),
                          showJobMatchingIcon: true,
                        } as any,
                        {
                          type: AttributeTable.columns.requirementType,
                          label: this.props.translate('tableColumns.priority'),
                          selectable: true,
                          selectableOnChild: false,
                        } as any,
                        {
                          type: AttributeTable.columns.requiredProficiency,
                          label: this.props.translate('addEditJob.minProficiency'),
                        },
                        { type: AttributeTable.columns.actionsMore },
                      ]}
                      onAttributeAction={this.onAttributeAction.bind(this) as any}
                      organization={organization}
                      keyword={searchCompetency ?? undefined}
                    />
                    {job!.numberOfCompetencies > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                        }}
                      >
                        {this.props.translate('addEditJob.totalCompetencies')}{' '}
                        {job!.numberOfCompetencies}
                      </Box>
                    )}
                    <AttributeActionMenu
                      ref={this.setMenuRef}
                      onClose={this.onCloseActionMenu.bind(this)}
                      onRender={this.renderActionMenu.bind(this)}
                    />
                  </Stack>
                </div>
              </Fade>
            )}

            <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
              <Button
                onClick={this.onCancel.bind(this)}
                label={this.props.translate('common.cancel')}
              />

              <Stack direction='row' spacing={2}>
                {step > 0 && !editMode && (
                  <Button
                    variant='outlined'
                    onClick={this.onBack.bind(this)}
                    startIcon={<LeftIcon />}
                    label={this.props.translate('addEditJob.back')}
                  />
                )}
                <Button
                  onClick={this.onSave.bind(this)}
                  variant='filled'
                  disabled={!this.isValid(job, step) || (editMode && !hasEdit)}
                  endIcon={!editMode && step !== 2 ? <RightIcon /> : null}
                  label={
                    editMode
                      ? this.props.translate('common.save')
                      : step !== 2
                        ? this.props.translate('addEditJob.continue')
                        : this.props.translate('common.create')
                  }
                />
              </Stack>
            </Stack>
          </Stack>

          {showAttributeSelectDialog && (
            <AttributeSelectorDialog
              theme={theme}
              excludedAttributes={job ? (job.attributes as any) : null}
              allowChildren={false}
              onLoadAttributes={onLoadAttributes as any}
              onShowNewAttributeDialog={onShowNewAttributeDialog}
              onClose={this.onCloseAttributeSelectDialog.bind(this)}
              onSave={this.onAttributesAdded.bind(this)}
            />
          )}

          {isShowTasksSelectDialog && (
            <TaskSelectorDialog
              theme={theme}
              existingTasks={get(job, 'regulationTasks', []) as any}
              onClose={this.onCloseTasksSelectDialog.bind(this)}
              onSave={this.onTasksAdded.bind(this)}
              subHeader={this.props.translate('addEditJob.selectTasksSubHeader')}
              placeholder={this.props.translate('placeholders.searchAndAdd')}
            />
          )}

          {isShowRelatedTasksInCompetencyDialog && (
            <RelatedTaskInAttributeDialog
              type={4}
              onClose={this.onCloseRelatedTasksInCompetencyDialog.bind(this)}
              theme={theme}
              tasksHasAttribute={tasksHasAttribute}
              {...this.props}
            />
          )}

          {isRemoveRelatedTasksInCompetencyDialog && (
            <ConfirmDeleteTaskDialog
              theme={theme}
              title={this.props.translate('addEditJob.deleteConfirmTitle')}
              content={<div>{this.props.translate('addEditJob.deleteConfirmContent')}</div>}
              onClose={this.onHideRemoveRelatedTasksInCompetencyDialog.bind(this)}
              onConfirm={this.onRemoveRelatedTasksInCompetency.bind(this)}
              onNotConfirm={this.onRemoveRelatedTasksButKeepCompetency.bind(this)}
            />
          )}
        </PaperOrDialog>
      </EditTemplate>
    );
  }
}

export const AddEditJobView = withLocale(AddEditJobViewBase);
